<template>
  <v-card flat width="100%" id="detail-table">
    <v-card-text class="pa-0">
      <v-dialog v-model="deleting" width="400px">
        <ConfirmDelete
        v-if="deleting"
        :title="$t('detail')"
        @delete="deleteItem"
        @cancel-delete="closeDelete"/>
      </v-dialog>
      <v-data-table
        :headers="detailHeaders"
        :items="activity.activityDetails"
        :hide-default-footer="activity.activityDetails.length < 10"
        :dense="true"
        :no-data-text="$t('noDetails')"
        class="small-headers"
        id="detail-data-table"
      >
        <template v-slot:item.cost.rate="{ item: detail }">
          <Icon
          v-if="detailHasScheduledChange(detail)"
          icon="invoice-text-update"
          iconColor="info"
          :tooltipText="scheduledRateChangeTooltip(detail)"/>
          {{ formatMoney(detail.cost.rate) }}
        </template>
        <template v-slot:item.cost.payOn="{ item }">{{
          payOnFromInt(item.cost.payOn)
        }}</template>
        <template v-slot:item.cost.payBy="{ item }">{{
          payByFromInt(item.cost.payBy)
        }}</template>
        <template v-if="showActions" v-slot:item.action="{ item }">
          <v-icon small color="success" class="mr-2" id="edit-detail" @click="editDetail(item)"
            >mdi-pencil</v-icon
          >
          <v-icon small color="error" id="delete-detail" @click="confirmDeleteDetail(item)"
            >mdi-delete</v-icon
          >
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { PayOn, PayBy } from '@/utils/Enumerations'
import { formatMoney } from '@/utils/NumericMutations'
import DetailHeaders from '@/headers/Detail'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { companyOffsetToLocalDate } from '@/utils/DateFormatter.js'

export default {
  name: 'DetailTable',

  components: {
    ConfirmDelete: () => import('@/components/helper/ConfirmDelete.vue'),
    Icon: () => import('@/components/helper/Icon.vue')
  },

  props: {
    activity: Object,
    contract: Object,
    showActions: Boolean
  },

  data: () => ({
    dialogId: uniqueDialogId('detail-table-dialog'),
    editing: false,
    focusedDetail: {},
    focusedProduct: null,
    deleting: false,
    viewingRateSheetDetails: undefined,
    focusedItem: null
  }),

  computed: {
    ...mapGetters('scheduled-rate-change', ['contractScheduledRateChanges']),
    ...mapGetters('tract-contents', ['allTractContents']),
    ...mapGetters('product', ['allProducts']),

    detailHeaders () {
      return this.showActions
        ? DetailHeaders.detailHeadersWithCRUD().filter(h => h.value !== 'action')
        : DetailHeaders.detailHeadersNoCRUD()
    }
  },

  methods: {
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('activity-detail', [
      'deleteActivityDetail', 'fetchRateSheetsForActivityDetail'
    ]),
    formatMoney,

    async deleteItem () {
      const detailRequestObj = {
        activityDetailId: this.focusedDetail.activityDetailId,
        contractId: this.contract.contractId,
        activityId: this.activity.activityId
      }
      await this.deleteActivityDetail(detailRequestObj)
      this.$emit('detail-deleted')
      this.closeDelete()
    },

    editDetail (detail) {
      detail.activityId = this.activity.activityId
      this.$emit('edit-detail', detail)
    },

    confirmDeleteDetail (detail) {
      this.focusedDetail = detail
      this.deleting = true
    },

    close () {
      this.$emit('close')
    },

    closeDelete () {
      this.deleting = false
      this.focusedDetail = {}
    },

    payByFromInt (val) {
      return PayBy.fromInt(val)
    },

    payOnFromInt (val) {
      return PayOn.fromInt(val)
    },

    detailHasScheduledChange (detail) {
      if (!detail.cost || !this.contractScheduledRateChanges) {
        return false
      }
      const activityChange = this.getScheduledChangeForActivity()
      if (!activityChange) return false

      return activityChange.newDetailRates.some(ndr => {
        return detail.product.productId === ndr.productId && detail.cost.rate !== ndr.newRate
      })
    },

    getScheduledChangeForActivity () {
      if (!this.contractScheduledRateChanges?.activityRateChanges) return null
      return this.contractScheduledRateChanges?.activityRateChanges.find(csrc => {
        return this.activity.activityId === csrc.activityId
      })
    },

    scheduledRateChangeTooltip (detail) {
      const activityChange = this.getScheduledChangeForActivity()
      const detailChange = activityChange.newDetailRates.find(ndr => {
        return detail.product.productId === ndr.productId
      })
      if (!detailChange) return this.$t('hasScheduledRateChange')
      const result = `${this.$t('hasScheduledRateChange')}: ${companyOffsetToLocalDate(detailChange?.effectiveDate, 'MM/DD/YYYY')} - ${formatMoney(detailChange.newRate)}`
      return result
    }
  }
}
</script>
